import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SharedService } from './shared.service';
import { SharedDataService } from './shared-data-service';
import { NotificationService } from './notification.service';
import { UserService } from '../../services/user.service';

@Injectable({ providedIn: 'root' })
export class UploadHandlerService {
  constructor(
    private sharedService: SharedService,
    private sharedDataService: SharedDataService,
    private notificationService: NotificationService,
    private userService: UserService
  ) {}

  private sanitizeFileNameForUpload(originalName: string): string {
    const name = String(originalName ?? '');
    if (!name) return 'upload';

    const lastDot = name.lastIndexOf('.');
    const base = lastDot > 0 ? name.slice(0, lastDot) : name;
    const ext = lastDot > 0 ? name.slice(lastDot + 1) : '';

    const sanitizedBase = base
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 120) || 'upload';

    const sanitizedExt = ext.replace(/[^a-zA-Z0-9]+/g, '').slice(0, 10);
    return sanitizedExt ? `${sanitizedBase}.${sanitizedExt}` : sanitizedBase;
  }

  /**
   * Build upload FormData from ppl-upload event and call SharedService.uploadFile.
   * Returns the SharedService observable so callers can subscribe and update UI.
   */
  uploadFromEvent(event: any, typeIn?: string): Observable<any> {
    const file = event?.file as File;
    if (!file) throw new Error('No file provided');

    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const type = typeIn ?? ((event?.flow ?? '') || '');
    const flowName = (type === 'authorizations') ? 'auth' : this.sharedDataService.getType(type);

    const formData = new FormData();
    const safeFileName = this.sanitizeFileNameForUpload(file.name);
    // Override the filename used in multipart Content-Disposition; keeps bytes identical.
    formData.append('file', file as any, safeFileName);

    const requestPayload: any = {
      fileType: file.name.split('.').pop() || '',
      otherType: (event?.documentType) || '',
      program,
      type: flowName,
      ...(flowName === 'auth' ? { authDetailId: event?.leadId } : { leadId: event?.leadId })
    };

    if ((event?.consumerId && event?.providerId) || (event?.source === 'purchaserequest' && event?.consumerId)) {
      Object.assign(requestPayload, {
        consumerId: event.consumerId,
        providerId: event.providerId,
        type: event.source || type
      });
    }

    formData.append('json', new Blob([JSON.stringify(requestPayload)], { type: 'application/json' }));

    // SharedService expects a string for the type — provide empty string if missing
    return this.sharedService.uploadFile(formData, String(type || ''));
  }

  deleteFile(program?: string, id?: any, createType?: string, entry?: string, silent = false): Observable<any> {
    return this.sharedService.deleteFile(program ?? '', id, createType ?? 'new', entry ?? '', silent);
  }

  downloadFile(file: any, entry?: string): void {
    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const flow = this.sharedDataService.getType(entry ?? '');
    const fileId = (file?.id ?? file?.fileId ?? file?.leadFileId ?? file?.file_id ?? file?.leadfileId);
    const fileName = file?.name ?? file?.fileName ?? file?.file_name ?? '';
    this.sharedService.downloadFile(program, flow, fileId, fileName);
  }

  handleUploadError(payload: any) {
    this.notificationService.error('Upload error', payload?.message ?? 'File upload failed');
  }
}
